import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import {
  listMotors, getSizeClass, gradeFromVelocity, severityForGrade,
  SEVERITY_COLORS, SEVERITY_BG, SEVERITY_STROKE,
  timeAgo, isOffline, cToF, offlineLabel, FADED_OPACITY, gradeRingFraction, fleetSummary, actionForGrade,
  trendFromHistory, historyValues, sparklineRange,
} from '../services/monitoringApi';
import tokens from '../design/tokens';

/**
 * Fleet overview — restyled to design/mockups/portal.mockup.html.
 *
 * RESTYLE, NOT A REWRITE: the fetch, the 5-minute auto-refresh, the props
 * (customerId / customerName / onSelectMotor / shopPhone) and every data
 * binding are unchanged. Only the presentation moved onto the design tokens.
 */

const C = tokens.color;
const F = tokens.font;
const R = tokens.radius;
const MONO = { fontFamily: F.mono };
const DISP = { fontFamily: F.display };

/* Derive a motor's reading set once, so the card and the tiles agree. */
function readMotor(motor) {
  const hp = motor.hp || motor.motorHp;
  const sizeClass = getSizeClass(hp);
  const velocity = motor.latestReading?.rmsVelocity;
  const temperature = motor.latestReading?.temperature;
  const timestamp = motor.latestReading?.timestamp;
  // Grade first, then colour from the grade — so the card, the tiles and the
  // PDF can never disagree about what letter a reading earned.
  const grade = gradeFromVelocity(velocity, hp);
  const severity = severityForGrade(grade);
  return {
    hp, sizeClass, velocity, temperature, timestamp, severity, grade,
    rpm: motor.latestReading?.rpm,
    offline: isOffline(timestamp),
    name: motor.motorName || motor.name || 'Unnamed Asset',
    location: motor.locationName || motor.location || '',
  };
}

function SkeletonCard() {
  return (
    <div style={{ padding: 18, borderRadius: R.xl, background: C.surface, border: `1px solid ${C.hairline}`, borderTop: `4px solid ${C.hairline}` }}>
      <div className="skeleton" style={{ height: 16, width: '60%', marginBottom: 10, borderRadius: 4 }} />
      <div className="skeleton" style={{ height: 12, width: '45%', marginBottom: 16, borderRadius: 4 }} />
      <div className="skeleton" style={{ height: 44, borderRadius: 6 }} />
    </div>
  );
}

/** 30-day sparkline. Uses real history when present, else a flat line at the
 *  current reading — never invents a trend the data does not show. */
/**
 * 60 daily means from monitoring-api. Drawn only when a trend can be computed
 * (trendFromHistory) — otherwise an empty strip of the same height, never a
 * made-up flat line.
 */
function Sparkline({ history, show, stroke }) {
  const vals = historyValues(history);
  if (!show || vals.length < 2) return <div style={{ height: 44 }} aria-hidden="true" />;
  const { lo, hi } = sparklineRange(vals);
  const d = vals.map((v, i) => {
    const x = (i / (vals.length - 1)) * 300;
    const y = 40 - ((v - lo) / (hi - lo)) * 36;
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(' ');
  return (
    <svg width="100%" height="44" viewBox="0 0 300 44" preserveAspectRatio="none" fill="none"
         stroke={stroke} strokeWidth="1.8" aria-hidden="true">
      <path d={d} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

export function MotorCard({ motor, onClick }) {
  const m = readMotor(motor);
  // An offline motor's last reading is not today's: grey card border, and its
  // LAST grade kept but faded (D2 #10) — the tiles and ring still count it
  // only as Offline.
  const status = m.offline ? null : m.severity;
  const stroke = status ? SEVERITY_STROKE[status] : C.hairlineStrong;
  const fg = status ? SEVERITY_COLORS[status] : C.textMuted;
  const chipFg = m.severity ? SEVERITY_COLORS[m.severity] : C.textMuted;
  const chipBg = m.severity ? SEVERITY_BG[m.severity] : C.surfaceAlt;
  const history = motor.history;
  // Offline: the last reading is not today's, so neither is its trend.
  const trend = m.offline ? null : trendFromHistory(history);

  return (
    <button
      onClick={onClick}
      style={{
        textAlign: 'left', font: 'inherit', cursor: 'pointer', width: '100%',
        padding: 18, borderRadius: R.xl, background: C.surface,
        border: `1px solid ${C.hairline}`, borderTop: `4px solid ${stroke}`,
        display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0,
      }}
      aria-label={m.offline
        ? `${m.name}, offline${m.grade ? `, last grade ${m.grade}` : ''}`
        : `${m.name}, grade ${m.grade || 'unknown'}`}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <div style={{ fontWeight: 600, fontSize: 15, color: C.ink, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</div>
        {(m.grade || m.offline) && (
          <div data-testid="grade-chip" title={m.offline ? 'Last grade — no current reading' : undefined} style={{ width: 30, height: 30, flexShrink: 0, borderRadius: R.base, background: chipBg, color: chipFg, opacity: m.offline ? FADED_OPACITY : 1, ...DISP, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {m.grade || '—'}
          </div>
        )}
      </div>

      <div style={{ fontSize: 12, color: C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {[m.location, m.hp ? `${m.hp} HP` : null, m.rpm ? `${Math.round(m.rpm)} RPM` : null].filter(Boolean).join(' · ')}
      </div>

      <Sparkline history={history} show={!!trend} stroke={stroke} />

      <div style={{ display: 'flex', justifyContent: 'space-between', ...MONO, fontSize: 13 }}>
        <span style={{ color: fg }}>{m.velocity != null ? `${m.velocity.toFixed(2)} mm/s` : '—'}</span>
        <span style={{ color: C.ink }}>{cToF(m.temperature) != null ? `${cToF(m.temperature)}°F` : '—'}</span>
        <span style={{ color: C.textMuted }} title={trend ? `Last 7 days vs first 7 of the last 60: ${trend.pct >= 0 ? '+' : ''}${trend.pct.toFixed(0)}%` : undefined}>
          {trend ? trend.label : ''}
        </span>
      </div>

      <div style={{ fontSize: 13, color: C.text, lineHeight: 1.45 }}>
        {m.offline ? offlineLabel(m.timestamp) : sentenceFor(m)}
      </div>
    </button>
  );
}

/** One plain sentence per motor — the PDF's words for the same grade. */
function sentenceFor(m) {
  return actionForGrade(m.grade) || 'Waiting on the first reading.';
}

function CountTile({ n, label, color }) {
  return (
    <div className="bs-tile-count" style={{ padding: 18, borderRadius: R.xl, background: C.surface, border: `1px solid ${C.hairline}`, display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <div style={{ ...DISP, fontWeight: 700, fontSize: 28, color }}>{n}</div>
      <div className="bs-tile-count-label" style={{ fontSize: 13, color: C.textMuted }}>{label}</div>
    </div>
  );
}

/** Fleet-health ring — the worst grade among REPORTING motors drives the letter. */
function FleetHealthTile({ worst }) {
  const letter = worst || '—';
  const pct = gradeRingFraction(worst);
  const circ = 138;

  return (
    <div className="bs-tile-ring" style={{ padding: 18, borderRadius: R.xl, background: C.ink, color: '#FFFFFF', display: 'flex', alignItems: 'center', gap: 14 }}>
      <svg width="52" height="52" viewBox="0 0 56 56" aria-hidden="true">
        <circle cx="28" cy="28" r="22" stroke="rgba(255,255,255,0.18)" strokeWidth="7" fill="none" />
        <circle cx="28" cy="28" r="22" stroke={C.accent} strokeWidth="7" fill="none"
                strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)} strokeLinecap="round"
                transform="rotate(-90 28 28)" />
        <text x="28" y="33" textAnchor="middle" fontFamily={F.display} fontWeight="800" fontSize="17" fill="#FFFFFF">{letter}</text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div style={{ ...DISP, fontWeight: 700, fontSize: 16 }}>Fleet health</div>
        <div style={{ fontSize: 12, color: C.textOnDarkMuted }}>Worst reading this week</div>
      </div>
    </div>
  );
}

export default function FleetOverview({ customerId, customerName, onSelectMotor, shopPhone }) {
  const [motors, setMotors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchMotors = useCallback(async (isAutoRefresh = false) => {
    try {
      if (!isAutoRefresh) setError(null);
      const result = await listMotors(customerId);
      setMotors(result.data || result.motors || []);
      setLastRefresh(new Date());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [customerId]);

  useEffect(() => { fetchMotors(); }, [fetchMotors]);

  useEffect(() => {
    const interval = setInterval(() => fetchMotors(true), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchMotors]);

  const handleRefresh = () => { setRefreshing(true); fetchMotors(); };

  const { good, watch, attention, critical, offline, worst } = fleetSummary(
    motors.map((m) => { const r = readMotor(m); return { grade: r.grade, offline: r.offline }; }),
  );
  const reporting = motors.length - offline;
  // With nothing reporting, the header states the newest reading's age, grey —
  // never "Just now" (that is the page's refresh time) and never a green dot.
  const newestReading = motors.reduce((max, m) => {
    const t = m.latestReading?.timestamp;
    return t && (!max || new Date(t) > new Date(max)) ? t : max;
  }, null);
  const noneReporting = motors.length > 0 && reporting === 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>
      <style>{`
        /* The ring tile gets the width its two lines need; the counts share the rest. */
        .bs-tiles { display: grid; grid-template-columns: minmax(230px, 1.6fr) repeat(5, minmax(0,1fr)); gap: 14px; }
        .bs-motors { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 14px; }
        @media (max-width: 1100px) { .bs-motors { grid-template-columns: repeat(2, minmax(0,1fr)); } }
        /* Below 900px: the ring takes the full row, the five counts sit in one
           row beneath it; on a phone, three then two. */
        @media (max-width: 900px)  { .bs-tiles { grid-template-columns: repeat(5, minmax(0,1fr)); gap: 10px; } .bs-tile-ring { grid-column: 1 / -1; } .bs-tile-count { padding: 14px !important; } }
        @media (max-width: 560px)  { .bs-tiles { grid-template-columns: repeat(3, minmax(0,1fr)); } }
        @media (max-width: 480px)  { .bs-tiles { gap: 8px; } .bs-tile-count { padding: 12px 10px !important; } .bs-tile-count-label { font-size: 12px !important; } }
        @media (max-width: 640px)  { .bs-motors { grid-template-columns: minmax(0,1fr); } }
      `}</style>

      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <div style={{ ...MONO, fontSize: 12, letterSpacing: F.tracking.eyebrow, color: C.link, textTransform: 'uppercase' }}>
            {customerName || 'Your fleet'}
          </div>
          <h1 style={{ margin: 0, ...DISP, fontWeight: 700, fontSize: 30, letterSpacing: F.tracking.tight, color: C.ink }}>Fleet</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: C.textMuted }}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: noneReporting ? C.textMuted : C.status.good }} />
            {noneReporting
              ? (newestReading ? `Last reading ${timeAgo(newestReading)}` : 'No reading yet')
              : (lastRefresh ? `Updated ${timeAgo(lastRefresh.toISOString()).replace(/^Just now$/, 'just now')}` : 'Loading…')}
            {/* Offline motors are not "reporting" — the count agrees with the Offline tile. */}
            {motors.length ? ` · ${reporting} of ${motors.length} asset${motors.length === 1 ? '' : 's'} reporting` : ''}
          </div>
          <button onClick={handleRefresh} disabled={refreshing}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 38, padding: '0 14px', borderRadius: R.base, background: C.surface, border: `1px solid ${C.hairline}`, color: C.text, fontSize: 13, fontWeight: 600, cursor: refreshing ? 'default' : 'pointer', fontFamily: F.body, opacity: refreshing ? 0.6 : 1 }}>
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {!loading && motors.length > 0 && (
        <div className="bs-tiles">
          <FleetHealthTile worst={worst} />
          <CountTile n={good} label="Good" color={C.status.good} />
          <CountTile n={watch} label="Watch" color={C.status.watch} />
          <CountTile n={attention} label="Attention" color={C.status.attention} />
          <CountTile n={critical} label="Critical" color={C.status.critical} />
          {/* Grey, like the PDF's "No data" chip — silence is not a status colour. */}
          <CountTile n={offline} label="Offline" color={C.textMuted} />
        </div>
      )}

      {error && (
        <div role="alert" style={{ background: C.errorBg, border: `1px solid ${C.status.attentionStroke}`, color: C.status.attention, borderRadius: R.md, padding: '12px 14px', fontSize: 14 }}>
          <AlertTriangle className="w-4 h-4 inline mr-2" />
          {error}
        </div>
      )}

      {loading && (
        <div className="bs-motors">
          {[1, 2, 3, 4, 5, 6].map((i) => <SkeletonCard key={i} />)}
        </div>
      )}

      {!loading && motors.length === 0 && !error && (
        <div style={{ background: C.surface, border: `1px solid ${C.hairline}`, borderRadius: R.xl, padding: '56px 24px', textAlign: 'center' }}>
          <div style={{ ...DISP, fontWeight: 700, fontSize: 18, color: C.ink, marginBottom: 8 }}>No assets reporting yet</div>
          <div style={{ fontSize: 14, color: C.textMuted }}>
            We're setting your sensors up. Call us at{' '}
            <a href="tel:4237904745" style={{ color: C.link }}>{shopPhone || '(423) 790-4745'}</a> with any question.
          </div>
        </div>
      )}

      {!loading && motors.length > 0 && (
        <div className="bs-motors">
          {motors.map((motor) => (
            <MotorCard key={motor.motorId || motor.id} motor={motor} onClick={() => onSelectMotor(motor)} />
          ))}
        </div>
      )}
    </div>
  );
}
